
<script>
import axios from 'axios'
import { apiErrorText } from '@/utils/http'
import authManager from '@/utils/auth'
import { isWechatBrowser, isMobileDevice, WX_LOGIN_MANUAL_KEY } from '@/utils/browser'
import {
  buildWechatQRLoginApiUrl,
  buildWechatOfficialLoginUrl,
  clearWechatSkipAutoLogin,
  continueSessionAfterAuth,
  isLoggedIn,
  shouldSkipWechatAutoLogin
} from '@/utils/loginFlow'
import {
  handleGuestClaimAfterAuth,
  readGuestClaimContext,
  stashGuestClaimContext
} from '@/utils/examClaim'
import { resolvePostLoginRoute } from '@/utils/referralAuth'
import { validatePassword, passwordsMatch, PASSWORD_HINT } from '@/utils/password'
import { startEmailCodeCooldownVue2 } from '@/utils/emailCodeCooldown'
import { createCaptchaRefresher } from '@/utils/captchaCooldown'

export default {
  name: 'LoginView',
  data() {
    return {
      username: '',
      password: '',
      captcha: '',
      captchaId: '',
      captchaImage: '',
      loading: false,
      wechatRedirecting: false,
      showManualLogin: false,
      showForgot: false,
      forgot: {
        email: '',
        emailCode: '',
        newPassword: '',
        confirmPassword: '',
        captcha: '',
        captchaId: '',
        captchaImage: ''
      },
      sendingForgotCode: false,
      forgotCodeCooldown: 0,
      resettingPassword: false,
      forgotTimer: null,
      loginMode: 'password',
      phone: '',
      phoneCode: '',
      sendingPhoneCode: false,
      phoneCodeCooldown: 0,
      phoneTimer: null
    }
  },
  computed: {
    wechatLoginUrl() {
      const { href, sid, examId, coupon, couponNo } = this.$route.query
      const c = typeof coupon === 'string' ? coupon : (typeof couponNo === 'string' ? couponNo : '')
      return buildWechatQRLoginApiUrl({
        origin: window.location.origin,
        href: typeof href === 'string' ? href : '',
        sid: typeof sid === 'string' ? sid : '',
        examId: examId != null ? String(examId) : '',
        coupon: c
      })
    },
    isWechatBrowser2() {
      return isWechatBrowser()
    },
    isMobile() {
      return isMobileDevice()
    },
    hasPendingGuestClaim() {
      return !!(this.$route.query.sid && this.$route.query.examId)
    },
    passwordHint() {
      return PASSWORD_HINT
    },
    registerLink() {
      return { path: '/register', query: { ...this.$route.query } }
    },
    wechatOfficialLoginUrl() {
      const ctx = readGuestClaimContext()
      const sid = this.$route.query.sid || ctx?.sid || ''
      const examId = this.$route.query.examId || ctx?.examId || ''
      if (sid && examId) {
        const redirect = `${window.location.origin}/h5/exam/result`
        return (
          `/api/v1/wechat/official/base/login?sid=${encodeURIComponent(sid)}` +
          `&examId=${encodeURIComponent(String(examId))}` +
          `&redirect=${encodeURIComponent(redirect)}`
        )
      }
      const href = this.$route.query.href
      const coupon = this.$route.query.coupon || this.$route.query.couponNo || ''
      return buildWechatOfficialLoginUrl({
        href: typeof href === 'string' ? href : '/',
        coupon: typeof coupon === 'string' ? coupon : ''
      })
    }
  },
  async created() {
    if (this.hasPendingGuestClaim) {
      stashGuestClaimContext({
        sid: this.$route.query.sid,
        examId: this.$route.query.examId
      })
    }
    if (isLoggedIn()) {
      try {
        await continueSessionAfterAuth(this.$router, this.$route)
      } catch (e) {
        this.$message.error(e.message || '跳转失败')
        this.showManualLogin = true
        await this.refreshCaptcha()
      }
      return
    }
    if (!shouldSkipWechatAutoLogin(this.$route)) {
      sessionStorage.removeItem(WX_LOGIN_MANUAL_KEY)
    }
    await this.refreshCaptcha()
    this.tryAutoWechatLogin()
  },
  beforeUnmount() {
    if (this.forgotTimer) clearInterval(this.forgotTimer)
    if (this.phoneTimer) clearInterval(this.phoneTimer)
  },
  methods: {
    // 图形验证码：防连续点击，上一次完成后 2s 内不再重复获取
    refreshCaptcha: createCaptchaRefresher(async function () {
      try {
        const response = await axios.get('/api/v1/user/captcha')
        if (response.data && response.data.data) {
          this.captchaId = response.data.data.captchaId
          this.captchaImage = response.data.data.imageBase64
        }
      } catch (err) {
        console.error('Failed to get captcha:', err)
      }
    }),
    focusNext(event, nextField) {
      if (nextField === 'loginBtn') {
        this.$refs.loginBtn.focus()
      } else {
        this.$refs[nextField + 'Input'].focus()
      }
    },
    async applyLoginSuccess(loginData, loginType) {
      clearWechatSkipAutoLogin()
      const userInfo = await authManager.handleLoginSuccess(loginData)
      console.log('login ok ', loginType, userInfo)
      this.$root.login(loginData, { loginType })

      try {
        const claimStatus = await handleGuestClaimAfterAuth(this.$router, this.$route)
        if (claimStatus === 'claimed' || claimStatus === 'recharge') {
          return true
        }
      } catch (claimErr) {
        this.$message.error(claimErr.message || '生成报告失败')
        return true
      }

      this.$message.success('登录成功')
      const href = typeof this.$route.query.href === 'string' ? this.$route.query.href : '/'
      const target = await resolvePostLoginRoute(href, { skipReferralForExam: true })
      this.$router.push(target)
      return true
    },
    async handleLogin() {
      if (!this.username || !this.password || !this.captcha) {
        this.$message.error('请填写完整信息')
        return
      }

      this.loading = true
      try {
        const response = await axios.post('/api/v1/user/login', {
          username: this.username,
          password: this.password,
          captcha: this.captcha,
          captchaId: this.captchaId,
        })

        if (response.data.code === 200 && response.data.data) {
          await this.applyLoginSuccess(response.data.data, 'password')
        } else if (response.data.code === 431) {
          this.$router.push({ path: '/security/captcha', query: { redirect: this.$route.fullPath } })
        } else {
          this.$message.error(response.data.message || '登录失败')
        }

      } catch (err) {
        console.error('Login failed:', err)
        this.$message.error(err.response?.data?.message || '登录失败')
        this.refreshCaptcha()
      } finally {
        this.loading = false
      }
    },
    validPhone(raw) {
      return /^1[3-9]\d{9}$/.test(String(raw || '').trim())
    },
    startPhoneCooldown() {
      startEmailCodeCooldownVue2(this, 'phoneTimer', 'phoneCodeCooldown', () => {
        this.refreshCaptcha()
      })
    },
    async sendPhoneLoginCode() {
      if (!this.validPhone(this.phone)) {
        this.$message.error('请填写11位手机号')
        return
      }
      if (!this.captcha || !this.captchaId) {
        this.$message.error('请填写图形验证码')
        return
      }
      if (this.phoneCodeCooldown > 0 || this.sendingPhoneCode) return
      this.sendingPhoneCode = true
      try {
        const response = await axios.post('/api/v1/user/sendphonecode', {
          phonenum: this.phone.trim(),
          captcha: this.captcha,
          captchaId: this.captchaId
        })
        if (response.data.code === 200) {
          this.$message.success('验证码已发送')
          this.startPhoneCooldown()
          this.captcha = ''
          await this.refreshCaptcha()
        } else {
          this.$message.error(apiErrorText(response.data, '发送失败'))
          await this.refreshCaptcha()
        }
      } catch (err) {
        this.$message.error(apiErrorText(err.response?.data, '发送失败'))
        await this.refreshCaptcha()
      } finally {
        this.sendingPhoneCode = false
      }
    },
    async handlePhoneLogin() {
      if (!this.validPhone(this.phone) || !this.phoneCode) {
        this.$message.error('请填写手机号与短信验证码')
        return
      }
      this.loading = true
      try {
        const response = await axios.post('/api/v1/user/phonecodelogin', {
          phonenum: this.phone.trim(),
          code: this.phoneCode.trim(),
          captcha: this.captcha,
          captchaId: this.captchaId
        })
        if (response.data.code === 200 && response.data.data) {
          await this.applyLoginSuccess(response.data.data, 'phone')
        } else if (response.data.code === 431) {
          this.$router.push({ path: '/security/captcha', query: { redirect: this.$route.fullPath } })
        } else {
          this.$message.error(apiErrorText(response.data, '登录失败'))
          this.refreshCaptcha()
        }
      } catch (err) {
        console.error('Phone login failed:', err)
        this.$message.error(apiErrorText(err.response?.data, '登录失败'))
        this.refreshCaptcha()
      } finally {
        this.loading = false
      }
    },
    onWechatClick() {
      if (this.hasPendingGuestClaim) {
        stashGuestClaimContext({
          sid: this.$route.query.sid,
          examId: this.$route.query.examId
        })
      }
    },
    tryAutoWechatLogin() {
      if (!isWechatBrowser() || isLoggedIn()) return
      if (shouldSkipWechatAutoLogin(this.$route)) {
        this.showManualLogin = true
        return
      }
      this.wechatRedirecting = true
      this.onWechatClick()
      window.location.href = this.wechatOfficialLoginUrl
    },
    onWechatOfficialClick() {
      clearWechatSkipAutoLogin()
      this.onWechatClick()
    },
    openForgot() {
      this.showForgot = true
      this.forgot = {
        email: '',
        emailCode: '',
        newPassword: '',
        confirmPassword: '',
        captcha: '',
        captchaId: '',
        captchaImage: ''
      }
      this.refreshForgotCaptcha()
    },
    // 忘记密码图形验证码：同样防连续点击（完成后 2s 冷却）
    refreshForgotCaptcha: createCaptchaRefresher(async function () {
      try {
        const response = await axios.get('/api/v1/user/captcha')
        if (response.data && response.data.data) {
          this.forgot.captchaId = response.data.data.captchaId
          this.forgot.captchaImage = response.data.data.imageBase64
        }
      } catch (err) {
        console.error('Failed to get captcha:', err)
      }
    }),
    startForgotCooldown() {
      startEmailCodeCooldownVue2(this, 'forgotTimer', 'forgotCodeCooldown', () => {
        this.refreshForgotCaptcha()
      })
    },
    async sendForgotCode() {
      if (!this.forgot.email) {
        this.$message.error('请先填写邮箱')
        return
      }
      if (!this.forgot.captcha) {
        this.$message.error('请填写图形验证码')
        return
      }
      if (this.forgotCodeCooldown > 0 || this.sendingForgotCode) return
      this.sendingForgotCode = true
      try {
        const response = await axios.post('/api/v1/user/sendemailcode', {
          email: this.forgot.email,
          type: 'forget',
          captcha: this.forgot.captcha,
          captchaId: this.forgot.captchaId
        })
        if (response.data.code === 200) {
          this.$message.success('验证码已发送到邮箱')
          this.startForgotCooldown()
          this.forgot.captcha = ''
          await this.refreshForgotCaptcha()
        } else {
          this.$message.error(response.data.message || '发送失败')
          await this.refreshForgotCaptcha()
        }
      } catch (err) {
        this.$message.error(err.response?.data?.message || '发送失败')
        await this.refreshForgotCaptcha()
      } finally {
        this.sendingForgotCode = false
      }
    },
    async submitForgot() {
      const f = this.forgot
      if (!f.email || !f.emailCode || !f.newPassword || !f.confirmPassword || !f.captcha) {
        this.$message.error('请填写完整信息')
        return
      }
      if (!passwordsMatch(f.newPassword, f.confirmPassword)) {
        this.$message.error('两次输入的新密码不一致')
        return
      }
      const pwdCheck = validatePassword(f.newPassword)
      if (!pwdCheck.ok) {
        this.$message.error(pwdCheck.message)
        return
      }
      this.resettingPassword = true
      try {
        const response = await axios.post('/api/v1/user/password/reset-by-email', {
          email: f.email,
          emailCode: f.emailCode,
          newPassword: f.newPassword,
          confirmPassword: f.confirmPassword,
          captcha: f.captcha,
          captchaId: f.captchaId
        })
        if (response.data.code === 200) {
          this.$message.success(response.data.message || '密码已重置，请使用新密码登录')
          this.showForgot = false
          this.username = f.email
          this.password = ''
          this.refreshCaptcha()
        } else {
          this.$message.error(response.data.message || '重置失败')
          await this.refreshForgotCaptcha()
        }
      } catch (err) {
        this.$message.error(err.response?.data?.message || '重置失败')
        await this.refreshForgotCaptcha()
      } finally {
        this.resettingPassword = false
      }
    }
  }
}
</script>

<template>
  <div class="login" :class="{ 'login--mobile': isMobile }">
    <div v-if="isWechatBrowser2 && wechatRedirecting && !showManualLogin" class="wechat-auto-redirect">
      <div class="wechat-auto-redirect__spinner" />
      <p>正在跳转微信登录…</p>
    </div>

    <template v-else>
    <h2 v-if="!isMobile && !(isWechatBrowser2 && !showManualLogin)">登录</h2>

    <el-alert
      v-if="hasPendingGuestClaim"
      type="info"
      :closable="false"
      show-icon
      class="guest-alert"
      title="您已完成测评"
      :description="isWechatBrowser2
        ? '将使用微信一键登录为您生成测评报告。也可改用账号密码或短信验证码登录。'
        : '登录或注册后，将自动为您生成测评报告。没有账号可使用邮箱注册、短信验证码登录，或使用微信扫码登录。'"
    />
    <div v-if="!isWechatBrowser2 || showManualLogin" class="login-mode-tabs">
      <button
        type="button"
        class="mode-tab"
        :class="{ 'mode-tab--active': loginMode === 'password' }"
        @click="loginMode = 'password'"
      >账号密码</button>
      <button
        type="button"
        class="mode-tab"
        :class="{ 'mode-tab--active': loginMode === 'phone' }"
        @click="loginMode = 'phone'"
      >短信验证码</button>
    </div>
    <form v-if="(!isWechatBrowser2 || showManualLogin) && loginMode === 'password'" @submit.prevent="handleLogin">
      <div>
        <label for="username">登录名</label>
        <input
          type="text"
          id="username"
          v-model="username"
          required
          autocomplete="username"
          @keydown.tab.prevent="focusNext($event, 'password')"
          @keydown.enter.prevent="focusNext($event, 'password')"
          ref="usernameInput"
        >
      </div>
      <div>
        <label for="password">密码</label>
        <input
          type="password"
          id="password"
          v-model="password"
          required
          autocomplete="current-password"
          @keydown.tab.prevent="focusNext($event, 'captcha')"
          @keydown.enter.prevent="focusNext($event, 'captcha')"
          ref="passwordInput"
        >
        <button type="button" class="forgot-link" @click="openForgot">忘记密码？</button>
      </div>
      <div class="captcha-container">
        <label for="captcha">图形验证码</label>
        <div class="captcha-input-group">
          <input 
            type="text" 
            id="captcha" 
            v-model="captcha" 
            required
            @keydown.tab.prevent="focusNext($event, 'loginBtn')"
            @keydown.enter.prevent="handleLogin"
            ref="captchaInput"
          >
          <img 
            :src="captchaImage" 
            alt="图形验证码"
            class="captcha-image"
            @click="refreshCaptcha"
          >
        </div>
      </div>
      <button
        type="submit"
        ref="loginBtn"
        :disabled="loading"
        @keydown.tab.prevent="focusNext($event, 'username')"
      >
        {{ loading ? '登录中…' : '登录' }}
      </button>
    </form>

    <form v-if="(!isWechatBrowser2 || showManualLogin) && loginMode === 'phone'" @submit.prevent="handlePhoneLogin">
      <p class="phone-login-hint">已绑定手机可直接登录；未注册号码验证通过后将自动创建账号。</p>
      <div>
        <label for="phone">手机号</label>
        <input
          type="tel"
          id="phone"
          v-model="phone"
          maxlength="11"
          required
          autocomplete="tel"
          placeholder="请输入11位手机号"
        >
      </div>
      <div class="captcha-container">
        <label for="phoneCaptcha">图形验证码</label>
        <div class="captcha-input-group">
          <input
            type="text"
            id="phoneCaptcha"
            v-model="captcha"
            required
          >
          <img
            :src="captchaImage"
            alt="图形验证码"
            class="captcha-image"
            @click="refreshCaptcha"
          >
        </div>
      </div>
      <div>
        <label for="phoneCode">短信验证码</label>
        <div class="captcha-input-group">
          <input
            type="text"
            id="phoneCode"
            v-model="phoneCode"
            maxlength="6"
            required
            autocomplete="one-time-code"
            placeholder="6 位验证码"
          >
          <button
            type="button"
            class="send-code-btn"
            :disabled="sendingPhoneCode || phoneCodeCooldown > 0"
            @click="sendPhoneLoginCode"
          >
            {{ phoneCodeCooldown > 0 ? `${phoneCodeCooldown}s` : (sendingPhoneCode ? '发送中…' : '发送验证码') }}
          </button>
        </div>
      </div>
      <button type="submit" :disabled="loading">
        {{ loading ? '登录中…' : '登录' }}
      </button>
    </form>

    <p v-if="!isMobile || (isWechatBrowser2 && showManualLogin)" class="register-hint">
      没有账号？
      <router-link :to="registerLink">邮箱注册</router-link>
    </p>

    <div v-if="!isMobile || isWechatBrowser2" class="social-login" :class="{ 'social-login--mobile': isMobile }">
      <h3 v-if="!isMobile && (!isWechatBrowser2 || showManualLogin)">或使用微信登录</h3>

      <a
        v-if="!isWechatBrowser2 && !isMobile"
        :href="wechatLoginUrl"
        class="wechat-btn"
        @click="onWechatClick"
      >
        <img src="https://res.wx.qq.com/a/wx_fed/assets/res/OTE0YTAw.png" alt="微信" class="wechat-icon">
        微信扫码登录
      </a>
      <template v-if="isWechatBrowser2 && showManualLogin">
        <a
          :href="wechatOfficialLoginUrl"
          class="wechat-btn wechat-btn--block"
          @click="onWechatOfficialClick"
        >
          <img src="https://res.wx.qq.com/a/wx_fed/assets/res/OTE0YTAw.png" alt="微信" class="wechat-icon">
          微信一键登录
        </a>
        <p class="wechat-manual-hint">
          <router-link :to="registerLink">邮箱注册</router-link>
        </p>
      </template>
    </div>
    <p v-else-if="isMobile" class="mobile-wechat-hint">
      <router-link :to="registerLink">邮箱注册</router-link>
    </p>
    </template>

    <el-dialog
      v-model="showForgot"
      title="邮箱找回密码"
      width="420px"
      :append-to-body="true"
      @closed="forgot.captcha = ''"
    >
      <p class="forgot-hint">仅已绑定并验证的真实邮箱可用。微信扫码占位邮箱无法找回。</p>
      <el-form label-position="top">
        <el-form-item label="邮箱">
          <el-input v-model="forgot.email" placeholder="请输入绑定邮箱" autocomplete="email" />
        </el-form-item>
        <el-form-item label="图形验证码">
          <div class="captcha-input-group">
            <el-input v-model="forgot.captcha" placeholder="图形验证码" />
            <img
              :src="forgot.captchaImage"
              alt="图形验证码"
              class="captcha-image"
              @click="refreshForgotCaptcha"
            >
          </div>
        </el-form-item>
        <el-form-item label="邮箱验证码">
          <div class="code-row">
            <el-input v-model="forgot.emailCode" placeholder="6 位验证码" maxlength="8" />
            <el-button
              :disabled="forgotCodeCooldown > 0"
              :loading="sendingForgotCode"
              @click="sendForgotCode"
            >
              {{ forgotCodeCooldown > 0 ? `${forgotCodeCooldown}s` : '发送验证码' }}
            </el-button>
          </div>
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="forgot.newPassword" type="password" show-password placeholder="请设置新密码" />
          <p class="field-hint">{{ passwordHint }}</p>
        </el-form-item>
        <el-form-item label="确认密码">
          <el-input v-model="forgot.confirmPassword" type="password" show-password placeholder="请再次输入新密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showForgot = false">取消</el-button>
        <el-button type="primary" :loading="resettingPassword" @click="submitForgot">重置密码</el-button>
      </template>
    </el-dialog>
  </div>
</template>


<style scoped>
.login {
  max-width: 400px;
  margin: 0 auto;
  padding: 20px;
}

.guest-alert {
  margin-bottom: 20px;
}

.login-mode-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.mode-tab {
  flex: 1;
  margin-top: 0;
  background: #f2f3f5;
  color: #606266;
}

.mode-tab:hover {
  background: #e4e7ed;
}

.mode-tab--active,
.mode-tab--active:hover {
  background: #42b983;
  color: #fff;
}

.phone-login-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}

.send-code-btn {
  width: auto;
  margin-top: 0;
  padding: 8px 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.register-hint {
  margin-top: 16px;
  text-align: center;
  color: #666;
}

.forgot-link {
  display: inline-block;
  margin-top: 6px;
  padding: 0;
  width: auto;
  background: none;
  color: #42b983;
  border: none;
  font-size: 13px;
  cursor: pointer;
}

.forgot-link:hover {
  background: none;
  text-decoration: underline;
}

.forgot-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #909399;
}

.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
}

.code-row {
  display: flex;
  gap: 8px;
}

form div {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
}

input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.captcha-container {
  margin-bottom: 20px;
}

.captcha-input-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.captcha-image {
  height: 40px;
  cursor: pointer;
  border: 1px solid #ddd;
  border-radius: 4px;
}

button {
  padding: 10px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
  width: 100%;
}

button:hover {
  background-color: #3aa876;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.social-login {
  margin-top: 30px;
  text-align: center;
}

.social-login h3 {
  color: #666;
  margin-bottom: 15px;
  position: relative;
}

.social-login h3:before,
.social-login h3:after {
  content: "";
  position: absolute;
  top: 50%;
  width: 30%;
  height: 1px;
  background-color: #ddd;
}

.social-login h3:before {
  left: 0;
}

.social-login h3:after {
  right: 0;
}

.wechat-btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  background-color: #07C160;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.wechat-btn:hover {
  background-color: #06ad56;
}

.wechat-icon {
  width: 24px;
  height: 24px;
  margin-right: 10px;
}

.login--mobile {
  max-width: none;
  margin: 0;
  padding: 16px;
  min-height: calc(100vh - 56px);
  box-sizing: border-box;
  background: #fff;
  border-radius: 12px 12px 0 0;
}

.login--mobile form {
  margin-top: 8px;
}

.social-login--mobile {
  margin-top: 20px;
}

.wechat-btn--block {
  display: flex;
  width: 100%;
  justify-content: center;
  box-sizing: border-box;
}

.mobile-wechat-hint {
  margin-top: 20px;
  text-align: center;
  font-size: 14px;
  color: #666;
}

.mobile-wechat-hint .dot {
  margin: 0 8px;
  color: #ccc;
}

.link-wechat {
  color: #07c160;
  text-decoration: none;
}

.wechat-auto-redirect {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  color: #606266;
  gap: 16px;
}

.wechat-auto-redirect__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e4e7ed;
  border-top-color: #07c160;
  border-radius: 50%;
  animation: login-spin 0.8s linear infinite;
}

@keyframes login-spin {
  to { transform: rotate(360deg); }
}

.wechat-manual-hint {
  margin-top: 12px;
  text-align: center;
  font-size: 14px;
}
</style> 